import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Flex,
  Result,
  Select,
  Tag,
  Typography
} from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  ContentSpinner,
  MonoValue,
  QueryErrorAlert,
  SectionLabel
} from 'src/components'
import { clearConsentReturnPath } from 'src/utils/consentReturnPath'

import {
  canAllowConsent,
  consentScopesFor,
  initialConsentOrganization,
  isConsentExpiredError,
  redirectHost
} from './consentModel'
import {
  UnsafeConsentRedirectError,
  useMcpConsent,
  useMcpConsentDecision
} from './hooks'

const { Text, Title } = Typography

export const McpConsentPage = () => {
  const { t } = useTranslation()
  const { requestId = '' } = useParams<{ requestId: string }>()
  const {
    data: consent,
    error,
    isLoading,
    isError,
    refetch
  } = useMcpConsent(requestId)
  const decide = useMcpConsentDecision(requestId)
  const [organizationId, setOrganizationId] = useState<number | null>(null)
  // Everything the client asked for starts checked; the user unchecks what to withhold.
  const [withheld, setWithheld] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    clearConsentReturnPath()
  }, [])

  if (isLoading) return <ContentSpinner minHeight={320} />

  const expired =
    isConsentExpiredError(error) || isConsentExpiredError(decide.error)

  if (isError && !expired) {
    return (
      <Flex justify="center" style={{ padding: '32px 16px' }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <QueryErrorAlert onRetry={() => void refetch()} />
        </div>
      </Flex>
    )
  }

  if (expired || !consent) {
    return (
      <Result
        status="warning"
        title={t('workspace.aiConnectors.consent.expiredTitle')}
        subTitle={t('workspace.aiConnectors.consent.expiredBody')}
      />
    )
  }

  const selected = organizationId ?? initialConsentOrganization(consent)
  const scopes = consentScopesFor(consent, selected)
  const granted = scopes
    .map((scope) => scope.code)
    .filter((code) => !withheld.has(code))
  const serviceName = t(
    `workspace.aiConnectors.services.${consent.service}.name`
  )
  const hasOrganizations = consent.organizations.length > 0
  // The decision is already recorded by Core, so a refused redirect is final.
  const refused = decide.error instanceof UnsafeConsentRedirectError
  const busy = decide.isPending || decide.isSuccess || refused

  return (
    <Flex justify="center" style={{ padding: '32px 16px' }}>
      <Card style={{ width: '100%', maxWidth: 520 }}>
        <Flex vertical gap={16}>
          <Flex vertical gap={6}>
            <Title level={4} style={{ margin: 0 }}>
              {t('workspace.aiConnectors.consent.title', {
                client: consent.client.name
              })}
            </Title>
            <Flex align="center" gap={8} wrap="wrap">
              <Tag color="warning">
                {t('workspace.aiConnectors.consent.unverified')}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspace.aiConnectors.consent.redirectsTo', {
                  host: redirectHost(consent.client.redirect_uri)
                })}
              </Text>
            </Flex>
          </Flex>

          <Alert
            type="warning"
            showIcon
            title={t('workspace.aiConnectors.consent.unverifiedHint')}
          />

          <Descriptions
            column={1}
            size="small"
            items={[
              {
                key: 'service',
                label: t('workspace.aiConnectors.consent.service'),
                children: serviceName
              },
              {
                key: 'url',
                label: t('workspace.aiConnectors.connect.mcpUrl'),
                children: (
                  <MonoValue ellipsis copyable={false}>
                    {consent.mcp_url}
                  </MonoValue>
                )
              }
            ]}
          />

          {hasOrganizations ? (
            <>
              <Flex vertical gap={6}>
                <SectionLabel>
                  {t('workspace.aiConnectors.consent.workspace')}
                </SectionLabel>
                <Select
                  aria-label={t('workspace.aiConnectors.consent.workspace')}
                  value={selected ?? undefined}
                  placeholder={t(
                    'workspace.aiConnectors.consent.workspacePlaceholder'
                  )}
                  onChange={(value: number) => setOrganizationId(value)}
                  options={consent.organizations.map((org) => ({
                    value: org.id,
                    label: org.name
                  }))}
                />
              </Flex>

              {selected !== null ? (
                <Flex vertical gap={6}>
                  <SectionLabel>
                    {t('workspace.aiConnectors.consent.scopes')}
                  </SectionLabel>
                  {scopes.length ? (
                    <Flex vertical gap={4}>
                      {scopes.map((scope) => (
                        <Checkbox
                          key={scope.code}
                          checked={!withheld.has(scope.code)}
                          disabled={busy}
                          onChange={(e) => {
                            const next = new Set(withheld)
                            if (e.target.checked) next.delete(scope.code)
                            else next.add(scope.code)
                            setWithheld(next)
                          }}
                        >
                          {scope.description}
                        </Checkbox>
                      ))}
                    </Flex>
                  ) : (
                    <Text type="secondary">
                      {t('workspace.aiConnectors.consent.noScopes')}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('workspace.aiConnectors.consent.actsAsYou')}
                  </Text>
                </Flex>
              ) : null}
            </>
          ) : (
            <Alert
              type="info"
              showIcon
              title={t('workspace.aiConnectors.consent.noWorkspaceTitle')}
              description={t('workspace.aiConnectors.consent.noWorkspaceBody', {
                service: serviceName
              })}
            />
          )}

          {refused ? (
            <Alert
              type="error"
              showIcon
              role="alert"
              title={t('errors.somethingWentWrong')}
            />
          ) : null}

          <Flex justify="end" gap={8}>
            <Button
              disabled={busy}
              onClick={() => decide.mutate({ decision: 'deny' })}
            >
              {t('workspace.aiConnectors.consent.deny')}
            </Button>
            {hasOrganizations ? (
              <Button
                type="primary"
                loading={busy}
                disabled={
                  !canAllowConsent(consent, selected) || !granted.length
                }
                onClick={() =>
                  selected !== null &&
                  decide.mutate({
                    decision: 'approve',
                    organizationId: selected,
                    scopes: granted
                  })
                }
              >
                {t('workspace.aiConnectors.consent.allow')}
              </Button>
            ) : null}
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
